// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
// The axios configuration can be changed according to the project, just change the file, other files can be left unchanged
import axios from 'axios';
import eventBus from '/@/utils/eventBus';
import type { AxiosResponse } from 'axios';
import { formatNull } from '@basicai/tool-components/lib/utils';
import type { RequestOptions, Result } from '/#/axios';
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform';
import { VAxios } from './Axios';
import { checkStatus } from './checkStatus';
import { useGlobSetting } from '/@/hooks/setting';
import { useMessage } from '/@/hooks/web/useMessage';
import { RequestEnum, ResultEnum, ContentTypeEnum } from '/@/enums/httpEnum';
import { isString } from '/@/utils/is';
import { setObjToUrlParams, deepMerge } from '/@/utils';
import { useErrorLogStoreWithOut } from '/@/store/modules/errorLog';
import { t } from '/@/utils/i18n';
import { joinTimestamp, formatRequestDate } from './helper';
import { useUserStoreWithOut } from '/@/store/modules/user';
import { useLocale } from '/@/locales/useLocale';
import {
  expireTypeEnum,
  isExpireStore,
  isLicenseExpired,
  planExpireTypeEnum,
} from '/@/store/modules/teamExpire';
import { useModalForTokenExpired } from '/@/hooks/web/useModalForTokenExpired';
import qs from 'qs';

const globSetting = useGlobSetting();
const urlPrefix = globSetting.urlPrefix;
const { createMessage, createErrorModal } = useMessage();

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
  /**
   * @description: 处理请求返回的数据。如果数据不是预期格式，可直接抛出错误
   */
  transformRequestHook: (originRes: AxiosResponse<Result>, options: RequestOptions) => {
    const modalForTokenExpired = useModalForTokenExpired();
    const res = formatNull(originRes) as AxiosResponse<Result>;
    const { isTransformResponse, isReturnNativeResponse } = options;
    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
    if (isReturnNativeResponse) {
      return res;
    }
    // 不进行任何处理，直接返回
    // 用于页面代码可能需要直接获取code，data，message这些信息时开启
    if (!isTransformResponse) {
      return res.data;
    }
    // 错误的时候返回

    const { data: responseData } = res;
    // console.log(res);
    if (!responseData) {
      // return '[HTTP] Request has no return value';
      throw new Error(t('sys.api.apiRequestFailed'));
    }
    //  这里 code，result，message为 后台统一的字段，需要在 types.ts内修改为项目自己的接口返回格式
    const { code, data, message } = responseData;

    // 这里逻辑可以根据项目进行修改
    const hasSuccess =
      responseData && Reflect.has(responseData, 'code') && code === ResultEnum.SUCCESS;
    if (hasSuccess) {
      return data;
    }

    // 在此处根据自己项目的实际情况对不同的code执行不同的操作
    // 如果不希望中断当前请求，请return数据，否则直接抛出异常即可
    let timeoutMsg = '';
    switch (code) {
      case ResultEnum.LICENSE_EXPIRED:
        isLicenseExpired.value = true;
        break;
      case ResultEnum.USER__TEAM__NOT_EXIST:
        timeoutMsg = t('sys.api.disbandTeamTips');
        modalForTokenExpired('');
        break;
      case ResultEnum.USER__TEAM__NOT_HAVE_USER:
        timeoutMsg = t('sys.api.userExistTips');
        modalForTokenExpired('');
        break;
      case ResultEnum.USER__LOGIN_STATUS_TIMEOUT:
        timeoutMsg = t('sys.api.timeoutMessage');
        modalForTokenExpired('');
        break;
      case ResultEnum.ANNOTATION__TASK__TASK_STATUS_ERROR:
        timeoutMsg = 'Modifications to settings are not allowed in current task status.';
        break;
      case ResultEnum.USER__TEAM__EXPIRED:
        timeoutMsg = '';
        isExpireStore.isExpire = true;
        break;
      case ResultEnum.USER__TEAM__DISABLED:
        console.log('USER__TEAM__DISABLED');

        timeoutMsg = '';
        isExpireStore.isExpire = true;
        isExpireStore.type = expireTypeEnum.disable;
        break;
      case ResultEnum.USER__TEAM__MEMBER_IS_FULL:
        timeoutMsg = '';
        isExpireStore.isExpire = true;
        isExpireStore.planExpireType = planExpireTypeEnum.member;
        break;
      case ResultEnum.USER__STORAGE__SPACE_IS_FULL:
        timeoutMsg = '';
        isExpireStore.isExpire = true;
        isExpireStore.planExpireType = planExpireTypeEnum.storage;
        break;
      case ResultEnum.USER__STORAGE__SPACE_AND_TEAM__MEMBER_IS_FULL:
        timeoutMsg = '';
        isExpireStore.isExpire = true;
        isExpireStore.planExpireType = planExpireTypeEnum.memberAndStorage;
        break;
      case 'ERROR':
        timeoutMsg = t('sys.api.apiRequestFailed');
        break;
      default:
        if (message) {
          timeoutMsg = message;
        }
    }

    if (isExpireStore.isExpire) {
      return data;
    }

    // errorMessageMode=‘modal’的时候会显示modal错误弹窗，而不是消息提示，用于一些比较重要的错误
    // errorMessageMode='none' 一般是调用时明确表示不希望自动弹出错误提示
    if (options.errorMessageMode === 'modal') {
      if (timeoutMsg) {
        createErrorModal({ title: t('sys.api.errorTip'), content: timeoutMsg });
      }
    } else if (options.errorMessageMode === 'message') {
      if (timeoutMsg) {
        createMessage.error(timeoutMsg);
      }
    }

    throw new Error(timeoutMsg || t('sys.api.apiRequestFailed'));
  },

  // 请求之前处理config
  beforeRequestHook: (config, options) => {
    // console.log('beforeRequestHook =>', config);
    const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options;

    if (joinPrefix) {
      config.url = `${urlPrefix}${config.url}`;
    }
    if (apiUrl && isString(apiUrl)) {
      config.url = `${apiUrl}${config.url}`;
    }
    const params = config.params || {};
    const data = config.data || false;
    formatDate && data && !isString(data) && formatRequestDate(data);

    config.headers = config.headers ?? {};
    if (
      (config.url?.includes('/task') ||
        config.url?.includes('/dataset/data') ||
        config.url?.includes('/datasetClass/findByPage') ||
        config.url?.includes('/datasetClassification/findByPage') ||
        config.url?.includes('/dataset/quality/config/find') ||
        config.url?.includes('/performance')) &&
      config.params?.taskId
    ) {
      if (!config.url?.includes('/summary')) {
        config.headers['X-Current-Task-Id'] = config.params?.taskId || config.data?.taskId;
      }
    }
    config.headers['Accept-Language'] = useLocale().getLocale.value;

    if (config.method?.toUpperCase() === RequestEnum.GET) {
      if (!isString(params)) {
        // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
        config.params = Object.assign(params || {}, joinTimestamp(joinTime, false));
      } else {
        // 兼容restful风格
        config.url = config.url + params + `${joinTimestamp(joinTime, true)}`;
        config.params = undefined;
      }
    } else {
      if (!isString(params)) {
        formatDate && formatRequestDate(params);
        if (Reflect.has(config, 'data') && config.data && Object.keys(config.data).length > 0) {
          config.data = data;
          config.params = params;
        } else {
          // 非GET请求如果没有提供data，则将params视为data
          config.data = params;
          config.params = undefined;
        }
        if (joinParamsToUrl) {
          config.url = setObjToUrlParams(
            config.url as string,
            Object.assign({}, config.params, config.data),
          );
        }
      } else {
        // 兼容restful风格
        config.url = config.url + params;
        config.params = undefined;
      }
    }
    return config;
  },

  /**
   * @description: 请求拦截器处理
   */

  requestInterceptors: (config, options) => {
    // 中断请求
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    const userStore = useUserStoreWithOut();

    if (eventBus.state.tokenChanging) {
      config.cancelToken = source.token;
      source.cancel('Operation canceled by the user.');
    }

    // 请求之前处理config

    const token = userStore.getToken;
    if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
      // jwt token
      (config as Recordable).headers.Authorization = options.authenticationScheme
        ? `${options.authenticationScheme} ${token}`
        : token;
    }
    return config;
  },

  /**
   * @description: 响应拦截器处理
   */
  responseInterceptors: (res: AxiosResponse<any>) => {
    return res;
  },

  /**
   * @description: 响应错误处理
   */
  responseInterceptorsCatch: (error: any) => {
    const errorLogStore = useErrorLogStoreWithOut();
    errorLogStore.addAjaxErrorInfo(error);
    const { response, code, message, config, __CANCEL__ } = error || {};
    const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none';
    const msg: string = response?.data?.error?.message ?? '';
    const err: string = error?.toString?.() ?? '';
    let errMessage = '';
    const hideTimeOutMsg = config?.requestOptions?.hideTimeOutMsg;
    try {
      if (__CANCEL__) {
        // 只有用户手动cancel才会有这个标志，timeout等cancel是没有的
        // 如果是用户取消的请求，则不展示任何提示直接将异常抛出去
        return Promise.reject(error);
      }
      // // TODO 证书过期，退出登录
      // if (code === 'USER__LICENSE_EXPIRED') {
      //   createMessage.error(message);
      //   const userStore = useUserStore();
      //   return Promise.reject(error);
      // }
      if (code === 'ECONNABORTED' && message.indexOf('timeout') !== -1) {
        errMessage = hideTimeOutMsg ? '' : t('sys.api.apiTimeoutMessage');
      }
      if (err?.includes('Network Error')) {
        errMessage = t('sys.api.networkExceptionMsg');
      }
      // vue框架预内部抛出的预加载资源错误
      if (err?.includes('Error: Unable to preload')) {
        errMessage = t('sys.api.networkExceptionMsg');
      }

      if (errMessage) {
        if (errorMessageMode === 'modal') {
          createErrorModal({ title: t('sys.api.errorTip'), content: errMessage });
        } else if (errorMessageMode === 'message') {
          createMessage.error(errMessage);
        }
        return Promise.reject(error);
      }
    } catch (error) {
      console.log(error);
      throw new Error(error as unknown as string);
    }

    checkStatus(error?.response?.status, msg, errorMessageMode);
    return Promise.reject(error);
  },
};

function createAxios(opt?: Partial<CreateAxiosOptions>) {
  return new VAxios(
    deepMerge(
      {
        // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
        // authentication schemes，e.g: Bearer
        authenticationScheme: 'Bearer',
        // authenticationScheme: '',
        timeout: 10 * 1000,
        // 基础接口地址
        // baseURL: globSetting.apiUrl,

        headers: {
          'Content-Type': ContentTypeEnum.JSON,
        },
        // `paramsSerializer`是可选方法，主要用于序列化`params`
        // (e.g. https://www.npmjs.com/package/qs, http://api.jquery.com/jquery.param/)
        paramsSerializer: function (params) {
          // axios默认是{ arrayFormat: 'brackets' }，'a[]=b&a[]=c'
          // 改成{ arrayFormat: 'comma' }，'a=b,c'
          return qs.stringify(params, { arrayFormat: 'comma' });
        },
        // 如果是form-data格式
        // headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
        // 数据处理方式
        transform,
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
          // 默认将prefix 添加到url
          joinPrefix: true,
          // 是否返回原生响应头 比如：需要获取响应头时使用该属性
          isReturnNativeResponse: false,
          // 需要对返回数据进行处理
          isTransformResponse: true,
          // post请求的时候添加参数到url
          joinParamsToUrl: false,
          // 格式化提交参数时间
          formatDate: true,
          // 消息提示类型
          errorMessageMode: 'message',
          // 接口地址
          apiUrl: globSetting.apiUrl,
          // 接口拼接地址
          urlPrefix: urlPrefix,
          //  是否加入时间戳
          joinTime: true,
          // 忽略重复请求
          ignoreCancelToken: true,
          // 是否携带token
          withToken: true,
        },
      },
      opt || {},
    ),
  );
}
export const defHttp = createAxios();

// if (document.domain !== 'localhost') {
//   const list = window.location.hostname.split('.');
//   if (list.length > 3) {
//     document.domain = list.slice(list.length - 3).join('.');
//   } else {
//     document.domain = list.slice(list.length - 2).join('.');
//   }
// } else {
//   document.domain = 'localhost';
// }

// other api url
export const otherHttp = createAxios({
  requestOptions: {},
});
